import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { expensesService } from '../services/expenses.service';
import { categoriesService } from '../services/categories.service';
import toast from 'react-hot-toast';
import { FiPlus, FiFilter, FiDollarSign, FiCalendar, FiTag } from 'react-icons/fi';
import { tw, monoFont, expenseCategoryColors, getCategoryStyle } from '../utils/terminalTheme';

interface Expense {
  id: number;
  amount: number;
  expense_date: string;
  vendor?: string;
  description?: string;
  category?: {
    id: number;
    name: string;
    color_code?: string;
  };
  document?: {
    id: number;
    filename: string;
  };
}

interface Category {
  id: number;
  name: string;
  parent_id?: number;
}

export const BusinessExpenses: React.FC = () => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');

  useEffect(() => {
    loadBusinessExpenses();
    loadCategories();
  }, []);

  const loadBusinessExpenses = async () => {
    try {
      setLoading(true);
      const response = await expensesService.getExpenses();
      const businessExpenses = response.filter((expense: any) => !expense.property_id);
      setExpenses(businessExpenses);
    } catch (error) {
      toast.error('Failed to load business expenses');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await categoriesService.getExpenseCategories();
      setCategories(cats);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const filterExpenses = () => {
    let filtered = [...expenses];
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(exp => exp.category?.id === parseInt(selectedCategory));
    }
    if (dateFilter !== 'all') {
      const now = new Date();
      const startDate = new Date();
      switch (dateFilter) {
        case 'week': startDate.setDate(now.getDate() - 7); break;
        case 'month': startDate.setMonth(now.getMonth() - 1); break;
        case 'quarter': startDate.setMonth(now.getMonth() - 3); break;
        case 'year': startDate.setFullYear(now.getFullYear() - 1); break;
      }
      filtered = filtered.filter(exp => new Date(exp.expense_date) >= startDate);
    }
    return filtered;
  };

  const filteredExpenses = filterExpenses();
  const totalAmount = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);

  const categoryTotals = filteredExpenses.reduce((acc, exp) => {
    const categoryName = exp.category?.name || 'Uncategorized';
    if (!acc[categoryName]) {
      acc[categoryName] = { total: 0, count: 0, color: exp.category?.color_code };
    }
    acc[categoryName].total += exp.amount;
    acc[categoryName].count += 1;
    return acc;
  }, {} as Record<string, { total: number; count: number; color?: string }>);

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
          <span className="text-cyan-400">~/</span>expenses
          <span className="text-gray-600 text-sm font-normal ml-3">
            <span className="text-green-500">$</span> query --business
          </span>
        </h1>

        <div className="mt-3 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
          <p className="text-xs text-cyan-400/80 font-mono">
            Expenses are created automatically from processed documents with AI extraction. Each expense links to its original receipt/invoice.
          </p>
        </div>

        <div className="flex justify-between items-center mt-4">
          <p className="text-gray-500 text-xs font-mono">Viewing expenses across your property portfolio</p>
          <button
            onClick={() => navigate('/expenses/new?type=business')}
            className={tw.btnPrimary + ' flex items-center gap-2'}
          >
            <FiPlus className="w-4 h-4" />
            Add Expense
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className={tw.statCard}>
          <p className={tw.statLabel}>Total Expenses</p>
          <p className={tw.statValueCyan}>{formatCurrency(totalAmount)}</p>
        </div>
        <div className={tw.statCard}>
          <p className={tw.statLabel}>Number of Expenses</p>
          <p className="text-2xl font-bold font-mono text-green-500">{filteredExpenses.length}</p>
        </div>
        <div className={tw.statCard}>
          <p className={tw.statLabel}>Average Expense</p>
          <p className="text-2xl font-bold font-mono text-purple-400">
            {filteredExpenses.length > 0 ? formatCurrency(totalAmount / filteredExpenses.length) : '$0.00'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <FiFilter className="text-gray-600 w-3.5 h-3.5" />
            <span className="text-gray-500 text-xs font-mono uppercase tracking-wider">Filters</span>
          </div>
          <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className={tw.select + ' w-auto'}>
            <option value="all">All Categories</option>
            {categories.filter(cat => !cat.parent_id).map(category => (
              <option key={category.id} value={category.id.toString()}>{category.name}</option>
            ))}
          </select>
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className={tw.select + ' w-auto'}>
            <option value="all">All Time</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
        </div>
      </div>

      {/* Category Breakdown */}
      {Object.keys(categoryTotals).length > 0 && (
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5 mb-6">
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-4">Category Breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(categoryTotals).map(([category, data]) => {
              const style = getCategoryStyle(category, expenseCategoryColors);
              return (
                <div key={category} className="flex items-center justify-between p-2.5 bg-gray-900/50 rounded-lg border border-gray-700/30">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono ${style.bg} ${style.text} border ${style.border}`}>
                      {category}
                    </span>
                    <span className="text-gray-600 text-[10px] font-mono">({data.count})</span>
                  </div>
                  <span className="text-white text-xs font-mono font-medium">{formatCurrency(data.total)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Expenses Table */}
      <div className={tw.tableWrap}>
        <div className="px-5 py-3 border-b border-gray-700/50 flex items-center justify-between">
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider">Expense Details</h2>
          <span className="text-[10px] text-gray-600 font-mono">{filteredExpenses.length} records</span>
        </div>

        {loading ? (
          <div className={tw.empty}>Loading expenses...</div>
        ) : filteredExpenses.length === 0 ? (
          <div className={tw.empty}>No business expenses found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700/30">
                  <th className={tw.tableHeader}>Date</th>
                  <th className={tw.tableHeader}>Vendor</th>
                  <th className={tw.tableHeader}>Category</th>
                  <th className={tw.tableHeader}>Description</th>
                  <th className={tw.tableHeader}>Document</th>
                  <th className={tw.tableHeaderRight}>Amount</th>
                </tr>
              </thead>
              <tbody className={tw.tableDivider}>
                {filteredExpenses.map(expense => (
                  <tr
                    key={expense.id}
                    className={tw.tableRow + ' cursor-pointer'}
                    onClick={() => {
                      if (expense.document?.id) {
                        navigate(`/documents/${expense.document.id}`);
                      } else {
                        toast('No document attached to this expense');
                      }
                    }}
                  >
                    <td className={tw.tableCell}>
                      <span className="text-sm text-gray-400 font-mono">{formatDate(expense.expense_date)}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-sm text-white font-mono">{expense.vendor || 'N/A'}</span>
                    </td>
                    <td className={tw.tableCell}>
                      {expense.category && (() => {
                        const style = getCategoryStyle(expense.category.name, expenseCategoryColors);
                        return (
                          <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono ${style.bg} ${style.text} border ${style.border}`}>
                            {expense.category.name}
                          </span>
                        );
                      })()}
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-sm text-gray-400 font-mono">{expense.description || '-'}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-[10px] text-gray-500 font-mono">{expense.document?.filename || '-'}</span>
                    </td>
                    <td className={tw.tableCellRight}>
                      <span className="text-sm font-mono font-medium text-white">{formatCurrency(expense.amount)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
